package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.advaita.cloud.drive.shared.ArchiveProgressDto
import com.advaita.cloud.drive.shared.DeleteProgressDto
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.Status
import com.advaita.cloud.drive.ui.store.SharedRowInfo
import com.advaita.cloud.drive.ui.store.TableSortKey
import com.advaita.cloud.drive.ui.store.extractActionTip
import com.advaita.cloud.drive.ui.store.expiryLabel
import com.advaita.cloud.drive.ui.store.extractActionVerb
import com.advaita.cloud.drive.ui.store.isExtractable
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

@Composable
fun FileTable(
    files: List<FileDto>,
    onFolderClick: (FileDto) -> Unit,
    onDownload: (FileDto) -> Unit,
    onDelete: (FileDto) -> Unit,
    onPreview: (FileDto) -> Unit,
    selectedFileId: String?,
    sortKey: TableSortKey,
    sortAscending: Boolean,
    onSortClick: (TableSortKey) -> Unit,
    highlightId: String?,
    onHighlightShown: () -> Unit,
    /** Archive/Unarchive master flag — gates the checkbox column AND the 📂 action. */
    archiveEnabled: Boolean = false,
    selectedIds: Set<String> = emptySet(),
    /** ADV-1465: the checkbox click, and Cmd/Ctrl+click on the row. The second argument is
     *  whether Shift was held (a Cmd/Ctrl+click on the row always passes false). */
    onToggleSelected: (FileDto, Boolean) -> Unit = { _, _ -> },
    onToggleSelectAllVisible: () -> Unit = {},
    onExtract: (FileDto) -> Unit = {},
    /** File ids with an extract POST currently in flight — disables that row's
     *  📂 action so a double-click can't queue two extracts of the same archive. */
    extractingIds: Set<String> = emptySet(),
    /**
     * ADV-1330: opens the share dialog for a row. Null hides the Share action. [shareCapFor]
     * gives the S8 sentence that caps a row's Share action (a disabled control with one
     * sentence), or null when it is live.
     */
    onShare: ((FileDto) -> Unit)? = null,
    shareCapFor: (FileDto) -> String? = { null },
    /** False hides the Share action on a row whose caller holds no `share` (Shared with me). */
    shareVisible: (FileDto) -> Boolean = { true },
    /**
     * ADV-1330 F3, Shared with me: [sharedInfo] non-null adds the "Shared by" and "Access"
     * columns, [onLeave] adds Leave on a row the caller may leave, and [readOnly] hides the
     * selection column. [deleteVisible] and [extractVisible] say, per row, whether Delete and
     * Extract show (Shared with me passes the caller's grants, [SharedWithMeStore.rowActions]).
     */
    sharedInfo: ((FileDto) -> SharedRowInfo?)? = null,
    onLeave: ((FileDto) -> Unit)? = null,
    readOnly: Boolean = false,
    deleteVisible: (FileDto) -> Boolean = { true },
    extractVisible: (FileDto) -> Boolean = { true },
    emptyText: String = "This folder is empty. Use Upload or New folder to add something.",
) {
    val selectable = archiveEnabled && !readOnly
    Div(attrs = { classes("file-table") }) {
        if (files.isEmpty()) {
            Div(attrs = { classes("empty-folder") }) {
                Text(emptyText)
            }
        } else {
            // .has-selection keeps the checkbox column visible once anything is ticked,
            // instead of hiding it again the moment the pointer leaves the row.
            Table(attrs = { if (selectedIds.isNotEmpty()) classes("has-selection") }) {
                Thead {
                    Tr {
                        if (selectable) {
                            val allSelected = files.isNotEmpty() && files.all { it.id in selectedIds }
                            Th(attrs = { classes("select-col") }) {
                                CheckboxInput(checked = allSelected, attrs = {
                                    attr("aria-label", "Select all")
                                    attr(
                                        "data-tip",
                                        if (allSelected) "Clear the selection" else "Select everything in this folder",
                                    )
                                    onChange { onToggleSelectAllVisible() }
                                })
                            }
                        }
                        SortableTh("Name", TableSortKey.NAME, sortKey, sortAscending, onSortClick)
                        if (sharedInfo != null) {
                            Th { Text("Shared by") }
                            Th { Text("Access") }
                            Th { Text("Expires") }
                        }
                        Th { Text("Type") }
                        SortableTh("Size", TableSortKey.SIZE, sortKey, sortAscending, onSortClick)
                        Th { Text("Status") }
                        SortableTh("Created", TableSortKey.CREATED, sortKey, sortAscending, onSortClick)
                        Th(attrs = { classes("actions-col"); if (onLeave != null) classes("with-leave") }) { Text("") }
                    }
                }
                Tbody {
                    files.forEach { file ->
                        FileRow(
                            file, onFolderClick, onDownload, onDelete, onPreview,
                            selected = file.id == selectedFileId,
                            archiveEnabled = selectable,
                            extractShown = archiveEnabled && extractVisible(file),
                            // The archive progress badge follows the feature, not the checkbox column.
                            archiveOverlay = archiveEnabled,
                            deleteShown = deleteVisible(file),
                            checkedForArchive = file.id in selectedIds,
                            onToggleSelected = onToggleSelected,
                            onExtract = onExtract,
                            extracting = file.id in extractingIds,
                            onShare = onShare?.takeIf { shareVisible(file) },
                            shareCap = if (onShare != null) shareCapFor(file) else null,
                            sharedColumns = sharedInfo != null,
                            sharedRow = sharedInfo?.invoke(file),
                            onLeave = onLeave,
                        )
                    }
                }
            }
        }
    }

    // Scroll-to + flash for a search reveal (ADV-1215). Keyed on the id AND the
    // listing: navigation sets the id before refresh() lands, so the first pass
    // often runs against the OLD folder's rows — the files key re-runs it when
    // the new listing arrives. Consumed exactly once on success; a stale id
    // whose row never appears (row deleted meanwhile) is overwritten by the
    // next reveal — deliberate no-op, not an error.
    LaunchedEffect(highlightId, files) {
        val id = highlightId ?: return@LaunchedEffect
        if (files.none { it.id == id }) return@LaunchedEffect
        val el = document.getElementById("row-$id") ?: return@LaunchedEffect
        el.asDynamic().scrollIntoView(js("({block: 'center'})"))
        // Out-of-band classList: Compose only rewrites `class` when the attrs map
        // changes, so this survives idle recompositions — but an attrs change on
        // this row inside the 2s window (e.g. it becomes selected) cuts the flash
        // short. Cosmetic; accepted.
        el.classList.add("row-flash")
        window.setTimeout({ el.classList.remove("row-flash") }, 2100)
        onHighlightShown()
    }
}

@Composable
private fun SortableTh(
    label: String,
    key: TableSortKey,
    activeKey: TableSortKey,
    ascending: Boolean,
    onClick: (TableSortKey) -> Unit,
) {
    val active = key == activeKey
    Th(attrs = {
        classes("sortable")
        attr("aria-sort", if (!active) "none" else if (ascending) "ascending" else "descending")
        onClick { onClick(key) }
    }) {
        Text(label)
        if (active) Span(attrs = { classes("sort-arrow") }) { Text(if (ascending) " ▲" else " ▼") }
    }
}

@Composable
private fun FileRow(
    file: FileDto,
    onFolderClick: (FileDto) -> Unit,
    onDownload: (FileDto) -> Unit,
    onDelete: (FileDto) -> Unit,
    onPreview: (FileDto) -> Unit,
    selected: Boolean,
    archiveEnabled: Boolean = false,
    checkedForArchive: Boolean = false,
    onToggleSelected: (FileDto, Boolean) -> Unit = { _, _ -> },
    onExtract: (FileDto) -> Unit = {},
    extracting: Boolean = false,
    onShare: ((FileDto) -> Unit)? = null,
    shareCap: String? = null,
    sharedColumns: Boolean = false,
    sharedRow: SharedRowInfo? = null,
    onLeave: ((FileDto) -> Unit)? = null,
    extractShown: Boolean = archiveEnabled,
    deleteShown: Boolean = true,
    archiveOverlay: Boolean = archiveEnabled,
) {
    val isFolder = file.kind == FileKind.FOLDER
    Tr(attrs = {
        // Both kinds are clickable: a folder navigates, a file opens the preview
        // pane. The Download and Delete buttons call stopPropagation, so a click
        // on either never reaches this handler.
        attr("id", "row-${file.id}")
        classes("clickable-row")
        if (selected) classes("selected-row")
        onClick { e ->
            // ADV-1465: Cmd+click (macOS) or Ctrl+click toggles the row, and the row stays shut.
            if (archiveEnabled && (e.metaKey || e.ctrlKey)) { onToggleSelected(file, false); return@onClick }
            if (isFolder) onFolderClick(file) else onPreview(file)
        }
    }) {
        if (archiveEnabled) {
            // stopPropagation: ticking a FOLDER row must not also navigate into it.
            Td(attrs = { classes("select-col"); onClick { it.stopPropagation() } }) {
                CheckboxInput(checked = checkedForArchive, attrs = {
                    attr("aria-label", "Select ${file.displayName}")
                    // ADV-1465: onClick, because a change event carries no Shift key. The Td
                    // above stops the click, so a tick never opens the row.
                    onClick { e -> onToggleSelected(file, e.shiftKey) }
                })
            }
        }
        Td {
            Icon(if (isFolder) IconName.FOLDER else IconName.FILE)
            Text(" ${file.displayName}")
        }
        if (sharedColumns) {
            Td(attrs = { classes("shared-by") }) { Text(sharedRow?.sharedBy ?: "—") }
            Td(attrs = { classes("shared-level") }) { Text(sharedRow?.level ?: "—") }
            val expiry = sharedRow?.let { expiryLabel(it.expiresAt, kotlin.js.Date.now()) }
            Td(attrs = { classes("shared-expires"); if (expiry?.soon == true) classes("expiry-soon") }) {
                Text(expiry?.text ?: "—")
            }
        }
        Td {
            Text(if (isFolder) "Folder" else (file.mimeType ?: "File"))
        }
        Td(attrs = { style { property("font-variant-numeric", "tabular-nums") } }) { Text(sizeCellText(file)) }
        Td {
            val p = file.pipelineProgress
            val tip = buildProgressTip(file.status, p)
            // Once the FILE is READY the pull is done (bytes committed) — suppress any pull
            // overlay regardless of a lingering/racing job-record state, so a bar or failure
            // badge can't ghost under a Ready file.
            val pull = file.pullProgress
            val pullError = pull?.error
            val pullFailed = file.status != Status.READY && pull?.status == "FAILED" && pullError != null
            Div(attrs = { if (tip != null && !pullFailed) attr("data-tip", tip) }) {
                if (pullFailed) {
                    // A failed pull commits nothing, so the FILE row is still PENDING and the
                    // two badges used to render side by side as "Pending  Pull failed" — a
                    // contradiction in which the amber Pending was the louder half. The pull's
                    // verdict is the whole story for this row, so collapse to one FAILED badge
                    // and keep the source error as its tooltip.
                    Span(attrs = { classes("badge", "failed"); attr("data-tip", pullError!!) }) { Text("Failed") }
                } else {
                    StatusBadge(file.status)
                    if (p != null && file.status == Status.PROCESSING) {
                        val pct = p.pct
                        Div(attrs = { classes("row-progress"); if (pct == null) classes("indeterminate") }) {
                            Div(attrs = {
                                classes("row-progress-fill")
                                if (pct != null) style { property("width", "${pct.coerceIn(0, 100)}%") }
                            }) {}
                        }
                    }
                    // A pulled file is Status.PENDING while downloading, so key off pullProgress
                    // presence (not Status.PROCESSING). Reuses the pipeline bar's CSS classes.
                    if (file.status != Status.READY &&
                        pull != null && pull.status in listOf("PENDING", "RUNNING")
                    ) {
                        val pct = pull.pct
                        Div(attrs = {
                            classes("row-progress"); if (pct == null) classes("indeterminate")
                            val barTip = if (pct != null) "Downloading ${pct.coerceIn(0, 100)}%" else "Downloading…"
                            attr("data-tip", barTip)
                        }) {
                            Div(attrs = {
                                classes("row-progress-fill")
                                if (pct != null) style { property("width", "${pct.coerceIn(0, 100)}%") }
                            }) {}
                        }
                    }
                }
            }
            // Extract/compress progress rides on the TARGET row: the destination folder
            // for an extract, or the archive file for a compress. Same treatment as a
            // pull — once the row is READY the job is done (bytes committed), so
            // suppress any overlay regardless of a lingering job record. Also gated on
            // the flag itself: if archive is switched OFF after a job was enqueued
            // (rollback scenario), the poller no-ops on that PENDING/RUNNING job forever,
            // so rendering it here would show a bar/badge that never resolves.
            //
            // Binding render contract (ArchiveStatusService KDoc): the bar shows ONLY for
            // PENDING/RUNNING, the failure badge ONLY for FAILED, and a clean SUCCEEDED
            // renders NOTHING. A non-null `error` on a SUCCEEDED job is a SKIP SUMMARY,
            // never a failure — it now gets its own informational note (never
            // .badge.failed), because a user who downloads an archive silently missing
            // files has no other way to learn that. A dest folder's own FileDto.status
            // never leaves NOT_APPLICABLE, which is why this keys off archiveProgress,
            // not file.status. See [archiveOverlayFor] for the branch table.
            val archive = file.archiveProgress
            when (archiveOverlayFor(archiveOverlay, file.status, archive)) {
                ArchiveOverlay.NONE -> Unit
                ArchiveOverlay.PROGRESS -> {
                    val pct = archivePct(archive!!)
                    Div(attrs = {
                        classes("row-progress"); if (pct == null) classes("indeterminate")
                        attr("data-tip", archiveTip(archive, pct))
                    }) {
                        Div(attrs = {
                            classes("row-progress-fill")
                            if (pct != null) style { property("width", "${pct.coerceIn(0, 100)}%") }
                        }) {}
                    }
                }
                // FAILED renders the badge regardless of whether error is set — a null
                // error must not mean a silent failure. All writers set error today, but
                // the fallback keeps that a property of this render code, not an
                // assumption about every future engine.
                ArchiveOverlay.FAILED -> {
                    val label = if (archive!!.kind == "EXTRACT") "Extract failed" else "Compress failed"
                    Span(attrs = { classes("badge", "failed"); attr("data-tip", archive.error ?: label) }) { Text(label) }
                }
                ArchiveOverlay.PARTIAL ->
                    Span(attrs = {
                        classes("badge", "skipped")
                        attr("data-tip", archive!!.error ?: "Some items were skipped")
                    }) { Text("ℹ Partial") }
            }
            // A folder being deleted in the background (FileDto.deleteProgress): a bar while the
            // worker removes its contents, a failure badge if it gave up. A finished delete
            // removes the folder row, so there is no success state to render.
            val deleting = file.deleteProgress
            when (deleting?.status) {
                "PENDING", "RUNNING" -> {
                    val pct = deletePct(deleting)
                    Div(attrs = {
                        classes("row-progress"); if (pct == null) classes("indeterminate")
                        attr("data-tip", deleteTip(deleting))
                    }) {
                        Div(attrs = {
                            classes("row-progress-fill")
                            if (pct != null) style { property("width", "${pct.coerceIn(0, 100)}%") }
                        }) {}
                    }
                }
                "FAILED" -> Span(attrs = {
                    classes("badge", "failed"); attr("data-tip", deleting.error ?: "Delete failed")
                }) { Text("Delete failed") }
            }
        }
        Td { Text(formatDate(file.createdAt)) }
        val leaveSlot = onLeave != null
        val slots = rowActionSlots(
            isFolder = isFolder,
            extract = extractShown && isExtractable(file),
            share = onShare != null,
            leave = onLeave != null && sharedRow?.leavable == true,
            delete = deleteShown,
            leaveSlot = leaveSlot,
        )
        Td(attrs = { classes("actions-col"); if (leaveSlot) classes("with-leave") }) {
            // Tim (2026-09-28): one fixed slot per action, in one order on every row, so the
            // icons line up in columns. An action that does not apply leaves its slot empty: a
            // span of the same width, not focusable and hidden from screen readers.
            slots.forEach { action ->
                if (action == null) {
                    Span(attrs = { classes("action-slot", "empty"); attr("aria-hidden", "true") })
                    return@forEach
                }
                Span(attrs = { classes("action-slot") }) {
                    when (action) {
                        RowAction.EXTRACT -> ExtractButton(file, extracting, onExtract)
                        RowAction.SHARE -> ShareButton(file, shareCap, onShare!!)
                        RowAction.DOWNLOAD -> IconAction("Download ${file.displayName}", IconName.DOWNLOAD) { onDownload(file) }
                        RowAction.LEAVE -> IconAction("Leave ${file.displayName}", IconName.LEAVE) { onLeave!!(file) }
                        RowAction.DELETE -> {
                            val label = if (isFolder) "Delete folder ${file.displayName}" else "Delete ${file.displayName}"
                            IconAction(label, IconName.TRASH, danger = true) { onDelete(file) }
                        }
                    }
                }
            }
        }
    }
}

/** The row actions of the file table, in their slot order ([actionSlotOrder]). */
enum class RowAction { EXTRACT, SHARE, DOWNLOAD, LEAVE, DELETE }

/**
 * Tim (2026-09-28): the fixed slots of the actions cell, left to right, the order the cell has
 * always drawn them in. [leaveSlot] (Shared with me, where Leave can show) adds Leave's slot
 * before Delete. The same slots on every row of one table, so the icons line up.
 */
fun actionSlotOrder(leaveSlot: Boolean): List<RowAction> =
    if (leaveSlot) listOf(RowAction.EXTRACT, RowAction.SHARE, RowAction.DOWNLOAD, RowAction.LEAVE, RowAction.DELETE)
    else listOf(RowAction.EXTRACT, RowAction.SHARE, RowAction.DOWNLOAD, RowAction.DELETE)

/**
 * One row's actions in [actionSlotOrder]: the action where it applies, null (an empty slot of
 * the same width) where it does not. Never shorter than the order. Download never applies to a
 * folder: a folder has no single blob, and "download a folder" is Compress.
 */
fun rowActionSlots(
    isFolder: Boolean,
    extract: Boolean,
    share: Boolean,
    leave: Boolean,
    delete: Boolean,
    leaveSlot: Boolean,
): List<RowAction?> = actionSlotOrder(leaveSlot).map { action ->
    val applies = when (action) {
        RowAction.EXTRACT -> extract
        RowAction.SHARE -> share
        RowAction.DOWNLOAD -> !isFolder
        RowAction.LEAVE -> leave
        RowAction.DELETE -> delete
    }
    action.takeIf { applies }
}

/** An icon button that stops the row's own click. [label] is its tip and its accessible name. */
@Composable
private fun IconAction(label: String, icon: IconName, danger: Boolean = false, onClick: () -> Unit) {
    Button(attrs = {
        if (danger) classes("btn", "icon", "danger") else classes("btn", "icon")
        attr("data-tip", label)
        attr("aria-label", label)
        onClick { event ->
            event.stopPropagation()
            onClick()
        }
    }) {
        Icon(icon)
    }
}

/**
 * Extract an archive into a new sibling folder, or decompress a plain .gz into a sibling file —
 * the verb and tooltip follow the source, so the control describes what the confirm dialog will
 * then offer. Disabled (with a spinner in place of the icon) while its own POST is in flight —
 * mirrors compressSubmitting — so a double-click can't queue two extracts of the same archive.
 */
@Composable
private fun ExtractButton(file: FileDto, extracting: Boolean, onExtract: (FileDto) -> Unit) {
    Button(attrs = {
        classes("btn", "icon")
        attr("data-tip", extractActionTip(file.displayName))
        attr("aria-label", "${extractActionVerb(file.displayName)} ${file.displayName}")
        if (extracting) attr("disabled", "")
        onClick { event ->
            event.stopPropagation()
            if (!extracting) onExtract(file)
        }
    }) {
        if (extracting) {
            Span(attrs = { classes("spinner") }) { Icon(IconName.REFRESH) }
        } else {
            Icon(IconName.EXTRACT)
        }
    }
}

/**
 * A cap is a control with one sentence (S8): the sentence is the tip, and a screen reader hears
 * it through aria-describedby. aria-disabled, not the native attribute, so the control stays
 * focusable and its reason reachable.
 */
@Composable
private fun ShareButton(file: FileDto, shareCap: String?, onShare: (FileDto) -> Unit) {
    val capId = "share-cap-${file.id}"
    Button(attrs = {
        classes("btn", "icon")
        if (shareCap != null) classes("capped")
        attr("data-tip", shareCap ?: "Share ${file.displayName}")
        attr("aria-label", "Share ${file.displayName}")
        if (shareCap != null) {
            attr("aria-disabled", "true")
            attr("aria-describedby", capId)
        }
        onClick { event ->
            event.stopPropagation()
            if (shareCap == null) onShare(file)
        }
    }) {
        Icon(IconName.SHARE)
    }
    if (shareCap != null) Span(attrs = { classes("sr-only"); attr("id", capId) }) { Text(shareCap) }
}

@Composable
private fun StatusBadge(status: Status) {
    val (label, cssClass) = when (status) {
        Status.READY -> "Ready" to "clean"
        Status.PROCESSING -> "Processing" to "scanning"
        Status.PENDING -> "Pending" to "pending"
        Status.NOT_APPLICABLE -> "—" to "skipped"
        Status.FAILED -> "Failed" to "failed"
    }
    Span(attrs = { classes("badge", cssClass) }) { Text(label) }
}

/**
 * Text for the size column. While a pull is in flight the file's own sizeBytes is still
 * null (it's set at finalize), so the cell would otherwise read "—" for the whole download.
 * Instead show live "<done> / <total>" — each side abbreviated independently by [formatSize]
 * (e.g. "10.4 MB / 88.8 GB"), so magnitude reads naturally at both ends. The bar conveys
 * proportion; this conveys scale (how much is left in absolute terms). Falls back to the
 * plain final size for READY/idle rows and when the source size is unknown.
 */
private fun sizeCellText(file: com.advaita.cloud.drive.shared.FileDto): String {
    val pull = file.pullProgress
    if (file.status != Status.READY && pull != null && pull.status in listOf("PENDING", "RUNNING")) {
        val done = pull.bytesDone
        val total = pull.sizeBytes
        if (done != null && total != null && total > 0L) {
            return "${formatSize(done)} / ${formatSize(total)}"
        }
        if (done != null) {
            // Non-range / not-yet-probed source: total unknown until the end.
            return "${formatSize(done)} / …"
        }
    }
    return formatSize(file.sizeBytes)
}

private fun buildProgressTip(status: Status, p: com.advaita.cloud.drive.shared.PipelineProgressDto?): String? {
    if (p == null) return null
    if (status == Status.FAILED) return p.error ?: "Pipeline failed"
    val parts = mutableListOf<String>()
    p.phase?.let { parts.add(it) }
    val issues = buildList {
        if (p.moduleWarn > 0) add("${p.moduleWarn} warn")
        if (p.moduleFail > 0) add("${p.moduleFail} fail")
    }
    if (issues.isNotEmpty()) parts.add(issues.joinToString(", "))
    return parts.joinToString(" · ").ifBlank { null }
}

/**
 * Percent for an archive job, or null for an indeterminate bar. Unlike a pull, the
 * backend sends no precomputed `pct`: a tar stream doesn't know its totals up front, so
 * entries_total/bytes_total are nullable. Prefer bytes (smooth), fall back to entries,
 * otherwise indeterminate.
 *
 * `internal` (not `private`) so ArchiveNamingTest (jsTest, associated with this
 * compilation) can exercise it directly — pure function, no DOM.
 */
/** What, if anything, an archive job puts in the status cell of its target row. */
internal enum class ArchiveOverlay { NONE, PROGRESS, FAILED, PARTIAL }

/**
 * The archive overlay branch, as a pure function so the table's one non-obvious
 * render decision is testable without a DOM.
 *
 * The rules, and why:
 *  - flag off, or no job on this row: nothing. If archive is switched OFF after a job
 *    was enqueued, the poller no-ops on it forever, so a bar here would never resolve.
 *  - PENDING/RUNNING, and the row itself is not yet READY: the progress bar. Once the
 *    row is READY the bytes are committed and any lingering job record is noise.
 *  - FAILED, same READY guard: the failure badge.
 *  - SUCCEEDED with a non-null error: the PARTIAL note. Deliberately NOT gated on the
 *    row's status — a compress output row is READY exactly when this matters, which is
 *    why the whole block used to short-circuit and the skip summary was never rendered.
 *  - SUCCEEDED with no error: nothing, permanently. That is the common case on every
 *    extract destination folder.
 */
internal fun archiveOverlayFor(
    archiveEnabled: Boolean,
    fileStatus: Status,
    archive: ArchiveProgressDto?,
): ArchiveOverlay {
    if (!archiveEnabled || archive == null) return ArchiveOverlay.NONE
    val settled = fileStatus == Status.READY
    return when {
        archive.status == "SUCCEEDED" && archive.error != null -> ArchiveOverlay.PARTIAL
        settled -> ArchiveOverlay.NONE
        archive.status == "PENDING" || archive.status == "RUNNING" -> ArchiveOverlay.PROGRESS
        archive.status == "FAILED" -> ArchiveOverlay.FAILED
        else -> ArchiveOverlay.NONE
    }
}

internal fun archivePct(p: ArchiveProgressDto): Int? {
    val totalBytes = p.bytesTotal
    if (totalBytes != null && totalBytes > 0L) {
        return ((p.bytesDone.toDouble() / totalBytes.toDouble()) * 100).toInt()
    }
    val totalEntries = p.entriesTotal
    if (totalEntries != null && totalEntries > 0L) {
        return ((p.entriesDone.toDouble() / totalEntries.toDouble()) * 100).toInt()
    }
    return null
}

/** Percent of the subtree deleted, or null for an indeterminate bar. Rows added later can pass 100. */
internal fun deletePct(p: DeleteProgressDto): Int? {
    val total = p.entriesTotal ?: return null
    if (total <= 0L) return null
    return ((p.entriesDone.toDouble() / total.toDouble()) * 100).toInt()
}

internal fun deleteTip(p: DeleteProgressDto): String {
    val total = p.entriesTotal
    return when {
        p.error != null -> "Deleting… ${p.error}"
        total != null && total > 0L -> "Deleting ${minOf(p.entriesDone, total)} of $total items"
        else -> "Deleting…"
    }
}

private fun archiveTip(p: ArchiveProgressDto, pct: Int?): String {
    val verb = if (p.kind == "EXTRACT") "Extracting" else "Compressing"
    return if (pct != null) "$verb ${pct.coerceIn(0, 100)}%" else "$verb…"
}

private val TRAILING_OFFSET = Regex(""".*T.*[+-]\d\d:?\d\d$""")

private fun formatDate(isoDate: String): String {
    if (isoDate.isBlank()) return ""
    // Backend emits ISO_INSTANT (trailing Z); force a UTC parse if the zone is
    // ever absent, then render in the browser's locale + timezone.
    val iso = if (isoDate.endsWith("Z") || isoDate.matches(TRAILING_OFFSET)) isoDate else "${isoDate}Z"
    val d = kotlin.js.Date(iso)
    return if (d.getTime().isNaN()) isoDate else d.toLocaleString()
}
